import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import './AppsTableRowV2.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppConnectionType,
  AppName,
  FinchApps,
  InstantAvailableStatus,
} from '../../../apps/definition';
import { AppState } from '../../../constants';
import { trackEvent } from '../../../extra/sharedMethods';
import { User, UserApp } from '../../../models/User';
import {
  getOrgAppData,
  getUserAppData,
  GOOGLE_APPS,
} from '../../../redux/selectors';
import { App } from '../../../scripts/app';
import { AnalyticsEvent } from '../../../scripts/constants/analytics-event';
import { useBoolState, useUserSafe } from '../../../scripts/hooks';
import { calculateIndexingPercentage } from '../../../scripts/utils';
import { UserImage } from '../../controls/UserImage/UserImage';
import { UIButton } from '../../controls/ui/UIButton/UIButton';
import { UIIcon } from '../../controls/ui/UIIcon/UIIcon';
import { UITooltip } from '../../controls/ui/UIToolTip/UIToolTip';
import { TableCell, TableRow } from '../../shadcn/lib/components/table';
import { useAppInstallController } from '../AppsTable/AppInstallController';
import {
  isInstantAvailable,
  showAppInOrgApps,
  showAppInPersonalApps,
} from '../AppsTable/AppUtils';

interface AppsTableRowProps {
  app: App;
}

const DisableButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <UIButton
      className="connectButton flipActive"
      onClick={onClick}
      size="small"
      type="warning"
    >
      Disconnect
    </UIButton>
  );
};

const ConnectedContent: React.FC<{ disableDisconnect?: boolean }> = ({
  disableDisconnect = false,
}) =>
  disableDisconnect ? (
    <UITooltip title="Your admin has connected the app.">
      <div className="connectedApp">
        <UIIcon name="check" />
        <span>Connected</span>
      </div>
    </UITooltip>
  ) : (
    <div className="connectedApp">
      <UIIcon name="check" />
      <span>Connected</span>
    </div>
  );

const PartiallyConnectedContent: React.FC = () => (
  <div className="connectedApp">
    <UIIcon className="loader" name="spinner-animated" />
    <span>Connecting</span>
  </div>
);

export const ConnectButton: React.FC<{
  onClick: () => void;
  text?: string;
}> = ({ onClick, text }) => {
  const [hovered, setHovered, unsetHovered] = useBoolState(false);
  return (
    <div onMouseEnter={setHovered} onMouseLeave={unsetHovered}>
      <UIButton
        className="connectButton"
        onClick={onClick}
        size="small"
        type={hovered ? 'primary' : 'secondary'}
      >
        {text ?? 'Connect'}
      </UIButton>
    </div>
  );
};

const comingSoon = <div className="comingSoon">Coming soon</div>;
const notAvailable = <div className="w-4 h-px bg-cloud-20 " />;
const notConnected = <div className="notConnected">Not connected</div>;
const adminRequired = (
  <UITooltip title="Please contact your admin to connect the app.">
    <div className="flex items-center gap-2 text-cloud-40 font-medium">
      <UIIcon name="lock" />
      Request admin
    </div>
  </UITooltip>
);

const finchAppAlreadyConnected = (
  <UITooltip title="Only one HRIS app is able to be connected at a time. Please disconnect the connected HRIS app in order to connect this app.">
    <div className="notAvailable">Not available</div>
  </UITooltip>
);

const notIndexingState = (
  <div className="connectionIssue">
    <UIIcon name="error" />
    <span>Not indexing</span>
  </div>
);

const contactAdmin = (
  <UITooltip title="Please contact your admin to reconnect the app.">
    <div className="connectionIssue gap-1">
      <UIIcon name="error" />
      <span>Error</span>
    </div>
  </UITooltip>
);

const IndexingButton: React.FC<{ shortname: AppName; isOrg: boolean }> = ({
  shortname,
  isOrg,
}) => {
  const user = useUserSafe();

  const appData = isOrg
    ? getOrgAppData(user, shortname)
    : getUserAppData(user, shortname);

  const percentage = calculateIndexingPercentage(appData);

  return (
    <div className="indexingApp">
      <UIIcon name="spinner-animated" />
      <span>{percentage === 0 ? 'Syncing' : `syncing (${percentage}%)`}</span>
    </div>
  );
};

const disablingState = (
  <div className="disablingApp">
    <UIIcon name="spinner-animated" />
    <span>Disabling</span>
  </div>
);

export const PersonalButton: React.FC<{
  app: App;
  appConnectionType: AppConnectionType;
  installEventName?: AnalyticsEvent;
  // eslint-disable-next-line complexity
}> = ({ app, appConnectionType, installEventName }) => {
  const [disabling, setDisabling] = useState(false);

  const appInstallController = useAppInstallController();
  const { shortname } = app.definition;
  const user = useUserSafe();
  const { appPreferences } = user.orgByOrg;

  const installCallback = useCallback(() => {
    const eventBody = {
      app: shortname,
      isOrg: false,
    };

    trackEvent(AnalyticsEvent.AppConnect, eventBody);

    if (installEventName) {
      trackEvent(installEventName, eventBody);
    }

    appInstallController.triggerAppInstallRequest(app, false);
  }, [appInstallController, app, shortname, installEventName]);

  const uninstallCallback = useCallback(() => {
    appInstallController.triggerAppUninstallRequest(app, false);
    trackEvent(AnalyticsEvent.AppDisconnect, {
      app: shortname,
      isOrg: false,
    });
  }, [app, appInstallController, shortname]);

  const personalAppData = getUserAppData(user, shortname);
  const orgAppData = getOrgAppData(user, shortname);

  useEffect(() => {
    setDisabling(false);
  }, [personalAppData?.statusCode]);

  /*
   * Org-level Google Workspace connections provide all data,
   * personal connections are not allowed.
   */

  if (
    appConnectionType === AppConnectionType.OrgOnly ||
    !showAppInPersonalApps(app.definition, appPreferences)
  ) {
    return notAvailable;
  }

  // if org app is connected, personal app is not required
  if (
    orgAppData &&
    (orgAppData.statusCode === AppState.Connecting ||
      orgAppData.statusCode === AppState.IndexingComplete) &&
    appConnectionType === AppConnectionType.AccessControl
  ) {
    return notAvailable;
  }

  const {
    definition: { shortname: appShortname },
    definition,
  } = app;

  const isInstant = isInstantAvailable(
    definition,
    InstantAvailableStatus.Personal
  );

  if (!personalAppData) {
    return <ConnectButton onClick={installCallback} />;
  }

  if (disabling) {
    return disablingState;
  }

  switch (personalAppData.statusCode) {
    case AppState.PartiallyConnected: {
      return <PartiallyConnectedContent />;
    }

    case AppState.Connecting:
    case AppState.Indexing:
    case AppState.IndexingFailed:
    case AppState.IndexingComplete: {
      if (
        isInstant ||
        personalAppData.statusCode === AppState.IndexingComplete
      ) {
        // hack to enable frontend to show connected status for instant apps

        return (
          <div className="flipHover">
            <span className="flipNormal">
              <ConnectedContent />
            </span>
            <DisableButton onClick={uninstallCallback} />
          </div>
        );
      }

      return <IndexingButton isOrg={false} shortname={appShortname} />;
    }

    case AppState.NotConnected:
    case AppState.ErrorDoNotReconnect:
      return notIndexingState;
    case AppState.ErrorReconnect: {
      return (
        <UIButton
          className="connectButton"
          onClick={installCallback}
          size="small"
          type="caution"
        >
          <UndoOutlinedIcon sx={{ fontSize: 16 }} />
          <span className="ml-2">Reconnect</span>
        </UIButton>
      );
    }

    case AppState.Disconnecting: {
      return disablingState;
    }
  }
};

const OrgAdminButton: React.FC<{
  app: App;
  appConnectionType: AppConnectionType;
  // eslint-disable-next-line complexity
}> = ({ app, appConnectionType }) => {
  const appInstallController = useAppInstallController();
  const [disabling, setDisabling] = useState(false);

  const { shortname, orgModeAvailable } = app.definition;
  const user = useUserSafe();
  const { appPreferences, usersAppsByOrg } = user.orgByOrg;
  const orgAppData = getOrgAppData(user, shortname);

  useEffect(() => {
    setDisabling(false);
  }, [orgAppData?.statusCode]);

  const orgInstallCallback = useCallback(() => {
    trackEvent(AnalyticsEvent.AppConnect, {
      app: shortname,
      isOrg: true,
    });

    appInstallController.triggerAppInstallRequest(app, true);
  }, [shortname, appInstallController, app]);

  const orgUninstallCallback = useCallback(() => {
    trackEvent(AnalyticsEvent.AppDisconnect, {
      app: shortname,
      isOrg: true,
    });

    appInstallController.triggerAppUninstallRequest(app, true);
  }, [app, appInstallController, shortname]);

  // App does not have org connection
  if (
    appConnectionType === AppConnectionType.PersonalOnly ||
    !showAppInOrgApps(app.definition, appPreferences)
  ) {
    return notAvailable;
  }

  if (!orgModeAvailable) {
    return comingSoon;
  }

  const {
    definition: { shortname: appShortname },
    definition,
  } = app;

  const isInstant = isInstantAvailable(definition, InstantAvailableStatus.Org);

  // Check if an other finch app is connected if so we don't allow to connect
  if (
    usersAppsByOrg.nodes.some(
      ({ appName }) =>
        FinchApps.includes(appName) &&
        appName !== shortname &&
        FinchApps.includes(shortname)
    )
  ) {
    return finchAppAlreadyConnected;
  }

  if (!orgAppData) {
    return <ConnectButton onClick={orgInstallCallback} />;
  }

  if (disabling) {
    return disablingState;
  }

  let { statusCode } = orgAppData;

  if (GOOGLE_APPS.has(shortname)) {
    statusCode = getGsuiteAdminStatus(orgAppData, user, shortname);
  }

  switch (statusCode) {
    case AppState.PartiallyConnected: {
      return <PartiallyConnectedContent />;
    }

    case AppState.Connecting:
    case AppState.Indexing:
    case AppState.IndexingFailed:
    case AppState.IndexingComplete: {
      if (isInstant || statusCode === AppState.IndexingComplete) {
        // hack to enable frontend to show connected status for instant apps

        if (GOOGLE_APPS.has(shortname)) {
          // Admins cannot disable Google Apps

          return <ConnectedContent disableDisconnect />;
        }

        return (
          <div className="flipHover">
            <span className="flipNormal">
              <ConnectedContent />
            </span>
            <DisableButton onClick={orgUninstallCallback} />
          </div>
        );
      }

      return <IndexingButton isOrg shortname={appShortname} />;
    }

    case AppState.NotConnected:
    case AppState.ErrorDoNotReconnect:
      return notIndexingState;
    case AppState.ErrorReconnect: {
      return (
        <UIButton
          className="connectButton"
          onClick={orgInstallCallback}
          size="small"
          type="caution"
        >
          <UIIcon name="error" />
          <span>Reconnect</span>
        </UIButton>
      );
    }

    case AppState.Disconnecting: {
      return disablingState;
    }
  }
};

const OrgUserStatus: React.FC<{
  app: App;
  appConnectionType: AppConnectionType;
}> = ({ app, appConnectionType }) => {
  const { shortname, orgModeAvailable } = app.definition;
  const user = useUserSafe();
  const orgAppData = getOrgAppData(user, shortname);

  // App does not have org connection
  if (appConnectionType === AppConnectionType.PersonalOnly) {
    return notAvailable;
  }

  if (!orgModeAvailable) {
    return (
      <div className="upcomingApp">
        <UIIcon name="time" />
        <span>Upcoming</span>
      </div>
    );
  }

  if (!orgAppData) {
    return adminRequired;
  }

  let { statusCode } = orgAppData;

  if (GOOGLE_APPS.has(shortname)) {
    statusCode = getGsuiteAdminStatus(orgAppData, user, shortname);
  }

  const {
    definition: { shortname: appShortname },
    definition,
  } = app;

  const isInstant = isInstantAvailable(definition, InstantAvailableStatus.Org);

  switch (statusCode) {
    case AppState.PartiallyConnected: {
      return notConnected;
    }

    case AppState.Connecting:
    case AppState.Indexing:
    case AppState.IndexingFailed:
    case AppState.IndexingComplete: {
      if (isInstant || statusCode === AppState.IndexingComplete) {
        // hack to enable frontend to show connected status for instant apps
        return <ConnectedContent disableDisconnect />;
      }

      return <IndexingButton isOrg shortname={appShortname} />;
    }

    // Generally not reachable but here as fallback
    case AppState.NotConnected:
    case AppState.ErrorDoNotReconnect:
    case AppState.ErrorReconnect:
      return contactAdmin;
    case AppState.Disconnecting: {
      return disablingState;
    }
  }
};

export const AppsTableRowV2: React.FC<AppsTableRowProps> = ({
  app,
}: AppsTableRowProps) => {
  const {
    definition: {
      shortname,
      displayName: name,
      icon,
      description: personalSearchDescription,
      connectionType,
    },
  } = app;

  const user = useUserSafe();

  const appConnectionType = connectionType || AppConnectionType.AccessControl;

  const { appPreferences } = user.orgByOrg;

  const socialProof = useMemo(() => {
    const socials = appPreferences.recommended_apps_list?.filter(
      (element) => element.name === shortname
    )[0];

    if (
      Array.isArray(socials?.social_info_list) &&
      socials?.social_info_list[0]
    ) {
      return {
        count: socials.count,
        social_list_info: socials.social_info_list,
      };
    }

    return { count: 0, social_list_info: [] };
  }, [appPreferences.recommended_apps_list, shortname]);

  return (
    <TableRow className="appsTableRowNew">
      <TableCell className="colApp">
        {icon ?? <UIIcon name={shortname} size={32} type="apps" />}
        <UITooltip
          title={`Search across ${personalSearchDescription as string}`}
        >
          <span>{name}</span>
        </UITooltip>
      </TableCell>
      <TableCell>
        <div className="colSocialProof">
          {socialProof.social_list_info.slice(0, 4).map((socialUser) => (
            <div className="userIcons" key={socialUser.email}>
              <UITooltip title={socialUser.email}>
                <div>
                  <UserImage
                    displayName={socialUser.name}
                    fontSize={12}
                    imageUrl={socialUser.icon}
                    size={24}
                  />
                </div>
              </UITooltip>
            </div>
          ))}
          {socialProof.count > 4 && (
            <div className="socialProofCount">+{socialProof.count - 4}</div>
          )}
        </div>
        {socialProof.social_list_info.length === 0 && notAvailable}
      </TableCell>
      <TableCell className="colOrg colButtonContainerNew">
        <div className="colButton">
          <div className="button">
            {user.admin ? (
              <OrgAdminButton app={app} appConnectionType={appConnectionType} />
            ) : (
              <OrgUserStatus app={app} appConnectionType={appConnectionType} />
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="colPersonal colButtonContainerNew">
        <div className="colButton">
          <div className="button">
            <PersonalButton app={app} appConnectionType={appConnectionType} />
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

function getGsuiteAdminStatus(
  orgAppData: UserApp,
  user: User,
  appShortname: AppName
): AppState {
  const personalAppData = getUserAppData(user, appShortname);
  const orgStatusCode = orgAppData.statusCode;

  // Case for admins who connected gsuite
  if (!personalAppData) {
    return orgAppData.statusCode;
  }

  const personalStatusCode = personalAppData.statusCode;

  // Case for gmail and gcal
  if (appShortname !== 'gdrive') {
    return personalStatusCode;
  }

  // Case for gdrive
  if (
    personalStatusCode === AppState.IndexingFailed ||
    orgStatusCode === AppState.IndexingFailed
  ) {
    return AppState.IndexingFailed;
  }

  if (
    personalStatusCode === AppState.ErrorReconnect ||
    orgStatusCode === AppState.ErrorReconnect
  ) {
    return AppState.ErrorReconnect;
  }

  if (
    personalStatusCode !== AppState.IndexingComplete ||
    orgStatusCode !== AppState.IndexingComplete
  ) {
    return AppState.Indexing;
  }

  return personalStatusCode;
}
